import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { Constants } from './constants';
import { BrowserHelper } from './helpers/browser.helper';
import { Session, initialSession } from './models/WebServer/session';
import { ErrorInfo } from './models/errorInfo';
import { AppConfigService } from './services/app.config.service';
import { I18Service } from './services/i18.service';
import { NotificationService } from './services/notification.service';
import * as sessionStatusActions from "./store/actions/sessionStatus.actions";
import { RootState } from './store/root.state';
import * as appSelectors from './store/selectors/app.selectors';
import * as sessionSelectors from './store/selectors/session.selectors';
import { AppState, initialAppState } from './store/states/app.state';
import { SessionStatusState } from './store/states/sessionStatus.state';

import * as selectorSessionStatus from "src/app/store/selectors/sessionStatus.selectors";
import { AppHelper, initWebViewDiagnostics } from './helpers/app.helper';
import { DisclaimerHelper } from './helpers/disclaimer.helper';
import { FileSenderHelper } from './helpers/fileSender.helper';
import { PartecipantRoles } from './models/WebServer/partecipantRoles';
import { PartecipantStatus, initialPartecipantStatus } from './models/WebServer/partecipantStatus';
import { initialSessionConfig } from './models/WebServer/sessionConfig';
import { StatusChangeEvent } from './models/statusChangeEvent';
import { CheckerService } from './services/checker.service';
import { FELoggerService, LogSource } from './services/feLogger.service';
import { SipService } from './services/sip.service';
import * as appActions from './store/actions/app.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy, AfterViewInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  errorInfo: ErrorInfo | undefined;
  AS: AppState = initialAppState;
  MYSTATUS: PartecipantStatus = initialPartecipantStatus;
  session: Session = initialSession;

  sessionStatus: SessionStatusState | undefined;
  afterInit = false;
  afterInitLock = false;
  title = AppHelper.getTitleName();

  @ViewChild('popupDownload',{static: false})
  popupDownload!: ElementRef;

  constructor(
    public config: AppConfigService,
    private checker: CheckerService,

    private readonly store: Store<RootState>,
    //instantiate
    private s: NotificationService,
    private l: FELoggerService,
    private d: DisclaimerHelper,
    private f: FileSenderHelper,
    private sipService: SipService,
    private transHelper: I18Service) {

    // [PERF-LOAD-06] AppComponent Constructor Start
    const perfNow = performance.now();
    const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
    console.log(`[PERF-LOAD-06] AppComponent Constructor Start | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

    // console.debug(`********** AppComponent constructor`)
    document.title = AppHelper.getTitleVersion();

    this.store.dispatch(appActions.loadingMessage({ message: I18Service.get("Loading.Configuration") }));

    this.store.select(appSelectors.selectAppState).pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.AS = data;
        this.errorInfo = data?.errorInfo;
        if (data.appReady && !this.afterInitLock) {
          // [PERF-LOAD-19] After Load Init Start
          const perfNow = performance.now();
          const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
          console.log(`[PERF-LOAD-19] After Load Init Start | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);
        }
        data.appReady && this.afterLoadInit();

        // console.debug(`******* appReady ${data.appReady}`);
        // NON resettare afterInitLock durante suspended mode per evitare re-init quando si riconnette
        if (!data.appReady && !data.isSuspended) {
          this.afterInit = false;
          this.afterInitLock = false;
          this.checker.stop();
        }
      });

    this.store.select(sessionSelectors.selectSession).pipe(takeUntil(this.destroy$))
      .subscribe(data => { this.session = data; });

    this.store.select(selectorSessionStatus.selectSessionStatus).pipe(takeUntil(this.destroy$))
      .subscribe(data => this.sessionStatus = data);


    this.store.select(selectorSessionStatus.myPartecipantStatus).pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      // this.sessionStatus = data;
      if (data) {

        const prevDisclaimerAccepted = this.MYSTATUS?.disclaimerAccepted;
        this.MYSTATUS = data;
        // Log cambiamenti di disclaimerAccepted
        if (prevDisclaimerAccepted !== data.disclaimerAccepted) {
          console.debug(`***** MYSTATUS.disclaimerAccepted changed: ${prevDisclaimerAccepted} => ${data.disclaimerAccepted}`);
        }
      }

      console.debug(`AppComponent myStatus`, data)
    });

  }
  ngAfterViewInit(): void {
    if(AppConfigService.role == PartecipantRoles.OnSiteOperator)
      initWebViewDiagnostics((data) => {
      // Invia al tuo backend/servizio di logging
      FELoggerService.log(`WebView Diagnostic`, LogSource.Debug, data);
    });
  }

  private afterLoadInit() {
    if (this.afterInitLock) return;
    // [PERF-LOAD-19b] After Load Init Executing
    const perfNow19b = performance.now();
    const elapsed19b = perfNow19b - ((window as any).perfMarks?.browserStart || 0);
    console.log(`[PERF-LOAD-19b] After Load Init Executing | Time: ${perfNow19b.toFixed(2)}ms | Elapsed: ${elapsed19b.toFixed(2)}ms`);

    console.log(`AppComponent afterLoadInit`)
    this.afterInitLock = true;
    const definedLang = I18Service.language || this.AS.myPartecipantInfo.language || this.session.config.agencySettings.defaultLanguage; //FORZATA LINGUA INIZIALE
    this.manageLanguageSet(definedLang).then((lang: string) => {
      // [PERF-LOAD-20] Translation Loaded
      const perfNow20 = performance.now();
      const elapsed20 = perfNow20 - ((window as any).perfMarks?.browserStart || 0);
      console.log(`[PERF-LOAD-20] Translation Loaded - lang: ${lang} | Time: ${perfNow20.toFixed(2)}ms | Elapsed: ${elapsed20.toFixed(2)}ms`);

      this.checkBrowserComp(this.AS.isCaller);
      console.log(`translation agencySetting language ${this.session.config.agencySettings.defaultLanguage} [${lang}]`)

      // [PERF-LOAD-21] Checker Started
      const perfNow21 = performance.now();
      const elapsed21 = perfNow21 - ((window as any).perfMarks?.browserStart || 0);
      console.log(`[PERF-LOAD-21] Checker Started | Time: ${perfNow21.toFixed(2)}ms | Elapsed: ${elapsed21.toFixed(2)}ms`);

      this.checker.init();
      this.store.dispatch(appActions.loadingMessage({message: ''}))

      this.afterInit = true;
      // [PERF-LOAD-22] APP FULLY INTERACTIVE ✓
      const perfNow22 = performance.now();
      const elapsed22 = perfNow22 - ((window as any).perfMarks?.browserStart || 0);
      console.log(`[PERF-LOAD-22] ✓✓✓ APP FULLY INTERACTIVE ✓✓✓ | Time: ${perfNow22.toFixed(2)}ms | Elapsed: ${elapsed22.toFixed(2)}ms`);
      console.log(`[PERF-SUMMARY] Total Load Time: ${elapsed22.toFixed(2)}ms`);

      // 🛡️ DISCLAIMER GUARD: Se disclaimer richiesto per caller, aspetta accettazione prima di avviare mode
      if (this.config.agencySettings.showDisclaimer && this.config.isCaller) {
        console.log(`AppComponent afterLoadInit - disclaimer required, waiting for acceptance before setting initial mode`);

        // Aspetta che disclaimerAccepted diventi true
        this.store.select(selectorSessionStatus.myPartecipantStatus).pipe(
          filter(status => status?.disclaimerAccepted === true),
          take(1), // Solo la prima volta
          takeUntil(this.destroy$)
        ).subscribe(() => {
          this.goToInitialAppMode();
        });
      } else {
        // No disclaimer richiesto o non è caller → procedi normalmente
        this.goToInitialAppMode();
      }

    });

    NotificationService.popupDownload = this.popupDownload.nativeElement;


  }
  private goToInitialAppMode() {
  const anyOpOnline = this.config.operatorsStatus.length > 0;
    const myRole = this.MYSTATUS.partecipantRole;
    const modes = AppHelper.getInitialAppMode(this.config.sessionConfig || initialSessionConfig, myRole, anyOpOnline);
    this.store.dispatch(appActions.setAppModes({ modes }));
    console.log(`AppComponent afterLoadInit - goToInitialAppMode modes:'${modes}' for ${myRole} and anyOpOnline: ${anyOpOnline}`);
    console.log(`[APPMODES] goToInitialAppMode → AS.appReady: ${this.AS.appReady}, errorInfo: ${!!this.AS.errorInfo}, MYSTATUS: ${!!this.MYSTATUS}`);
    this.afterInit = true;
  }

  private manageLanguageSet(defaultLan: string): Promise<string> {
    const navigatorLang = navigator.language.split('-')[0];
    let languageSet = Constants.DEFAULT_LANGUAGE;
    defaultLan = defaultLan || Constants.DEFAULT_LANGUAGE;

    if (Constants.ALLOWED_LANGUAGES.some(l => l === navigatorLang)) {
      console.info(`AppComponent manageLanguageSet using navigator ${navigatorLang} Language`);
      languageSet = navigatorLang;
    } else {
      console.info(`AppComponent manageLanguageSet using default Language --> ${defaultLan}. Navigator Language ${navigatorLang} not supported`);
      languageSet = defaultLan;
    }

    return this.transHelper.setLanguage(defaultLan);
  }



  private checkBrowserComp(isCaller: boolean) {

    const deviceControls = (isCaller) ? this.config.deviceControls : this.config.operatorDeviceControls;
    const resultBrowserComp = BrowserHelper.getBrowserCompatibility(deviceControls);
    const parsedUA = BrowserHelper.getParsedUa();

    var techErrors = BrowserHelper.checkBrowserTech();
    if (techErrors.length) {
      resultBrowserComp.success = false;
      resultBrowserComp.message += `\n${techErrors.join('\n')}`
    }
    // const language = (this.config.getUrlParam(Constants.PARAM_BROWSER_LANG) || navigator.language || (navigator as any)['userLanguage']).split('-')[0];
    // (isCaller) && I18Service.setNavigatorLanguage(language);

    const multiStatus = [
      { evt: StatusChangeEvent.UserLanguage, val: I18Service.language },
      { evt: StatusChangeEvent.BrowserControlResult, val: resultBrowserComp },
      { evt: StatusChangeEvent.DeviceInfoUpdate, val: parsedUA },
      { evt: StatusChangeEvent.PageInfoUpdate, val: AppHelper.getPageState() }
    ]

    this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.MultipleUpdateStatus, val: multiStatus }));

    // const operatorResultBrowserComp = BrowserHelper.checkBroserCompatibility(this.config.operatorDeviceControls);

    // this.config.browserCompatibility = new BrowserCompatibility(resultBrowserComp, techErrors);
    // this.config.operatorBrowserCompatibility = new BrowserCompatibility(operatorResultBrowserComp, []);
    // console.log('Browser COMP', this.config.browserCompatibility, this.config.operatorBrowserCompatibility, this.AS.parsedUA);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroy$.unsubscribe();

  }
  @HostListener('document:keydown.control.alt.shift.q', ['$event'])
  openDebug(e: KeyboardEvent | Event) {
    if(!(e instanceof KeyboardEvent)) return;
    e.preventDefault();
    this.sipService.showCurrentSessions();
  }
}

